import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { IFilterAllData, ListOfKeysFilterAllData } from 'src/typings/filter';
import { Actions } from 'src/store/app/actions';
import { getCrmData, getIsOpenAppartSidebar, getSelectedAppartment } from 'src/store/app/selectors';
import { Actions as FloorsActions } from 'src/store/floors/actions';
import { useBuildType, useGetFilteredData, useGetFilterParams } from 'src/hooks';

import { useContrastRatio } from 'src/hooks/useContrastRatio';
import { useSelectedDeal } from 'src/hooks/useSelectedDeal';
import { crossTableTitleItemSize } from 'src/constants';
import { useSelectedIds } from './hooks';
import { useIsPlacementNeedConnect } from './hooks/useIsPlacementNeedConnect';

interface IFloorItem {
  fillFloorsItem: IFilterAllData;
  floorItemText: string | null;
  fontSize: number;
}

export const FloorItem: React.FC<IFloorItem> = React.memo(({
  fillFloorsItem,
  floorItemText,
  fontSize,
}) => {
  const dispatch = useDispatch();
  const { isCrossTable } = useBuildType();
  const { selectedIds } = useSelectedIds();
  const { getColorByBgColor } = useContrastRatio();
  const isSelected = selectedIds.includes(fillFloorsItem.id);
  const crmData = useSelector(getCrmData);
  const [apartmentIsBlocked, setApartmentIsBlocked] = useState<boolean>(false);
  const { selectedHideParams } = useGetFilterParams(3);
  const { resultFiltering } = useGetFilteredData();

  const isPlacementNeedConnect = useIsPlacementNeedConnect(fillFloorsItem);
  const { isSelectedDeal } = useSelectedDeal();
  const selectedAppartment = useSelector(getSelectedAppartment);
  const isOpenAppartSidebar = useSelector(getIsOpenAppartSidebar);

  const isOpen = useMemo(() => {
    return isOpenAppartSidebar && selectedAppartment == fillFloorsItem[ListOfKeysFilterAllData.id];
  }, [selectedAppartment, isOpenAppartSidebar]);

  const selectedDeal = useMemo(() => {
    return isSelectedDeal(fillFloorsItem);
    // isSelectedDeal in deps: it closes over the store bitrixDeal — without it the
    // marker was computed once (rows loaded, deal not yet in store / stale after
    // link) and never refreshed until the row object itself changed.
  }, [fillFloorsItem, crmData, isSelectedDeal]);

  const id = (isOpen)
    ? 'floorItemPopover' : undefined;

  const bgColor = useMemo(() => {
    return isCrossTable
      ? (fillFloorsItem?.[ListOfKeysFilterAllData.placementStatusCrossTableColor]?.toString()
        || fillFloorsItem?.[ListOfKeysFilterAllData.placementStatusColor]?.toString() || '')
      : (fillFloorsItem?.[ListOfKeysFilterAllData.placementStatusColor]?.toString() || '');
  }, [isCrossTable, fillFloorsItem]);

  const floors = fillFloorsItem[ListOfKeysFilterAllData.placementFloor].length;

  useEffect(() => {
    setApartmentIsBlocked(((!isSelected && fillFloorsItem[ListOfKeysFilterAllData.placementStatusShowInModule] !== 'hide')
      || (!isSelected && fillFloorsItem[ListOfKeysFilterAllData.placementStatusShowInModule] === 'hide' && selectedHideParams.length > 0)
      || !resultFiltering.length));
  }, [isSelected, selectedHideParams, resultFiltering]);

  const handleClick = () => {
    if (!isSelected && !isCrossTable) return;

    if (isOpen) {
      dispatch(Actions.setIsOpenAppartSidebar(false));
      dispatch(FloorsActions.setSelectedApartmentId(undefined));
    } else {
      dispatch(FloorsActions.setSelectedApartmentId(fillFloorsItem[ListOfKeysFilterAllData.id]));
      dispatch(Actions.selectAppartment(fillFloorsItem[ListOfKeysFilterAllData.id]));
      dispatch(Actions.setIsOpenAppartSidebar(true));
    }
  };

  return (
    <div
      className="sectionContentWrapper"
      data-selected-cell={isOpen ? 'true' : undefined}
      data-deal-cell={selectedDeal ? 'true' : undefined}
    >
      <Box
        aria-describedby={id}
        bgcolor={bgColor}
        height={`calc(${crossTableTitleItemSize + ((crossTableTitleItemSize + 4) * (floors - 1))}px * var(--crosstable-size))`}
        className={
        `sectionContentItem ${apartmentIsBlocked && 'blockedItem'}
          ${selectedDeal && 'selectedDeal'}`
        }
        data-floors={ListOfKeysFilterAllData.placementFloor}
        position="relative"
        onClick={handleClick}
      >
        {isPlacementNeedConnect && (
          <Box
            position="absolute"
            bgcolor="#fff"
            borderRadius={50}
            top="2px"
            right="2px"
            style={{ width: 'calc(5px * var(--crosstable-size))', height: 'calc(5px * var(--crosstable-size))' }}
          />
        ) }
        <span style={{
          fontSize: `calc(${fontSize}px * var(--crosstable-size))`,
          fontWeight: 'bold',
          color: getColorByBgColor(bgColor),
        }}
        >
          {floorItemText}
        </span>
      </Box>
    </div>
  );
});
